import React, { useEffect, useRef, useState } from "react";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";

import { useAppSelector } from "../../../hooks/hooks";
import { plantillas } from "./ChatRoom";

interface MediaFile {
    file: File;
    base64: string;
    name: string;
    caption: string;
}

interface Props {
    setModalPlantilla: React.Dispatch<React.SetStateAction<boolean>>;
    plantillas: plantillas[];
    setPlantillas: React.Dispatch<React.SetStateAction<plantillas[]>>;
    handleEditPlantilla: (id: number) => void;
    selectedFiles: MediaFile[];
    setSelectedFiles: React.Dispatch<React.SetStateAction<MediaFile[]>>;
    nuevoMensaje: string;
    setNuevoMensaje: React.Dispatch<React.SetStateAction<string>>;
    setFileSeleccionadoIndex: React.Dispatch<
        React.SetStateAction<number | null>
    >;
}

const AditionalChatroomPlantilla = ({
    setModalPlantilla,
    plantillas,
    setPlantillas,
    handleEditPlantilla,
    selectedFiles,
    setSelectedFiles,
    nuevoMensaje,
    setNuevoMensaje,
    setFileSeleccionadoIndex,
}: Props) => {
    const casa = useAppSelector((state) => state.landing_house.house);
    const { id, tipo_usuario } = useAppSelector(
        (state) => state.usuario_logeado
    );

    const [paginaActual, setPaginaActual] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [isFetching, setIsFetching] = useState(false);

    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState(query);

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getPlantillas(1);
        setPlantillas([]);
    }, []);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedQuery(query);
        }, 500); // 500ms debounce

        return () => clearTimeout(timeout);
    }, [query]);

    useEffect(() => {
        setPlantillas([]); // limpiar anteriores
        setPaginaActual(1); // reiniciar paginación
        getPlantillas(1);
    }, [debouncedQuery]);

    const getPlantillas = async (page: number) => {
        setIsFetching(true);
        let id_casa = 0;
        let id_corredor = 0;

        if (tipo_usuario === 6) {
            id_corredor = id;
        }

        if (tipo_usuario === 3 || tipo_usuario === 8) {
            id_casa = casa.id;
        }

        const response = await fetchAxiosToken({
            url: `/plantillas_whatsapp/getAll/${page}/${id_casa}/${id_corredor}/?query=${debouncedQuery}`,
        });
        console.log("Respuesta de plantillas:", response);
        setPlantillas((prev) =>
            page === 1 ? response.rows : [...prev, ...response.rows]
        );
        setTotalRegistros(response.count);
        setPaginaActual(page);
        setIsFetching(false);
    };

    const handleScroll = () => {
        if (!scrollRef.current || isFetching) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

        if (scrollTop + clientHeight >= scrollHeight - 100) {
            // Si no hemos cargado todos los registros
            if (plantillas.length < totalRegistros) {
                getPlantillas(paginaActual + 1);
            }
        }
    };

    const fetchFileFromUrl = async (
        url: string,
        filename: string,
        mimeType = "application/octet-stream",
        caption: string
    ) => {
        const response = await fetch(url);
        const blob = await response.blob();
        const file = new File([blob], filename, { type: mimeType });

        return new Promise<{
            file: File;
            base64: string;
            name: string;
            caption: string;
        }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = (reader.result as string).split(",")[1];
                resolve({ file, base64, name: filename, caption });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const getMimeTypeFromExtension = (filename: string) => {
        const ext = filename.split(".").pop()?.toLowerCase();
        switch (ext) {
            case "jpg":
            case "jpeg":
                return "image/jpeg";
            case "png":
                return "image/png";
            case "pdf":
                return "application/pdf";
            case "doc":
                return "application/msword";
            case "docx":
                return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            default:
                return "application/octet-stream";
        }
    };

    const sendPlantilla = async (plantilla: plantillas) => {
        setNuevoMensaje("");
        setSelectedFiles([]);

        if (plantilla.tipo === "imagen" && Array.isArray(plantilla.imagenes)) {
            const files = await Promise.all(
                plantilla.imagenes.map(async (imagen) => {
                    const url = `${process.env.REACT_APP_PROXY}/plantillas_whatsapp/getImagen/${imagen.imagen}`;
                    return await fetchFileFromUrl(
                        url,
                        imagen.imagen,
                        "image/jpeg",
                        imagen.caption
                    );
                })
            );
            setSelectedFiles(files);
            setFileSeleccionadoIndex(0);
        } else if (
            plantilla.tipo === "archivo" &&
            Array.isArray(plantilla.archivos)
        ) {
            const files = await Promise.all(
                plantilla.archivos.map(async (archivo) => {
                    const url = `${process.env.REACT_APP_PROXY}/plantillas_whatsapp/getArchivo/${archivo.archivo}`;
                    // opcional: detectar tipo MIME según extensión
                    const mimeType = getMimeTypeFromExtension(archivo.archivo);
                    return await fetchFileFromUrl(
                        url,
                        archivo.archivo,
                        mimeType,
                        archivo.caption
                    );
                })
            );
            setSelectedFiles(files);
            setFileSeleccionadoIndex(0);
        } else if (plantilla.tipo === "mensaje") {
            setNuevoMensaje(plantilla.mensaje);
        }
    };

    return (
        <div className="w-full">
            <div className="flex justify-center items-center mt-5">
                <div
                    onClick={() => setModalPlantilla(true)}
                    className="border-[#029BFC] border text-[#029BFC] text-xs flex justify-center items-center gap-3 p-2 rounded-lg cursor-pointer hover:scale-105"
                >
                    Nuevo{" "}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        className="w-5 h-5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4.5v15m7.5-7.5h-15"
                        />
                    </svg>
                </div>
            </div>

            <div className="flex justify-center items-center mt-5 w-11/12 mx-auto">
                <input
                    type="text"
                    placeholder="Buscar plantillas..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="border border-[#029BFC] rounded-lg p-2 w-[250px] text-sm focus:outline-none focus:ring-1 focus:ring-[#029BFC]"
                />
            </div>
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="w-full h-[50vh] overflow-y-auto pb-20"
            >
                <div className="w-11/12 mx-auto grid grid-cols-2 mt-5 gap-3">
                    {plantillas.map((plantilla) => (
                        <div
                            key={plantilla.id}
                            className="bg-[#029BFC] bg-opacity-80 shadow-lg rounded-lg p-2 flex flex-col justify-between items-center text-white gap-3 "
                        >
                            <div
                                onClick={() =>
                                    handleEditPlantilla(plantilla.id)
                                }
                                className="text-sm font-semibold capitalize flex justify-between items-start w-full hover:underline hover:scale-105 cursor-pointer  "
                            >
                                <div className="text-xs">
                                    {plantilla.titulo}
                                </div>
                                {plantilla.tipo === "imagen" && (
                                    <div className="p-1 rounded-full bg-green-500">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth="1.5"
                                            stroke="currentColor"
                                            className="w-4 h-4 text-white"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                                            />
                                        </svg>
                                    </div>
                                )}

                                {plantilla.tipo === "archivo" && (
                                    <div className="p-1 rounded-full bg-red-500">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth="1.5"
                                            stroke="currentColor"
                                            className="w-4 h-4 text-white"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </div>

                            <div
                                onClick={() => sendPlantilla(plantilla)}
                                className="text-xs cursor-pointer hover:scale-105 bg-blue-500 rounded-lg p-2 mt-3"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                                    />
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>

                {isFetching && (
                    <div className="text-center text-sm text-gray-500 py-4">
                        Cargando más...
                    </div>
                )}
            </div>
        </div>
    );
};

export default AditionalChatroomPlantilla;
